/**
 * AuthContext — OAuth 2.0/OIDC authentication state for the dashboard.
 *
 * Provides:
 *   - Auth state: user, organisation, membership, organisations list
 *   - hasPermission(resource, action): check JWT/membership permissions
 *   - login(): initiate Work OS OAuth PKCE flow
 *   - logout(): revoke session and clear cookies
 *   - switchOrg(): switch active organisation
 *   - Auto-refreshes access token 2 minutes before expiry (AC#29)
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  work_os_id: string;
}

export interface AuthOrganisation {
  id: string;
  name: string;
}

export interface AuthPermission {
  resource: string;
  action: string;
}

export interface AuthMembership {
  role: string;
  permissions: AuthPermission[];
  joined_at: string;
}

export interface UserOrganisation {
  id: string;
  name: string;
  slug: string | null;
  logo_url: string | null;
  role: string;
  tier: string;
}

export type ConnectionType = 'google_oauth' | 'microsoft_oauth' | 'saml';

interface AuthState {
  user: AuthUser | null;
  organisation: AuthOrganisation | null;
  membership: AuthMembership | null;
  organisations: UserOrganisation[];
  isAuthenticated: boolean;
  isLoading: boolean;
  degraded: boolean;
}

interface AuthContextValue extends AuthState {
  login: (redirectUri: string, connectionType: ConnectionType, orgId?: string) => Promise<void>;
  logout: () => Promise<void>;
  switchOrg: (orgId: string) => Promise<void>;
  hasPermission: (resource: string, action: string) => boolean;
  refreshAuth: () => Promise<void>;
}

// ── Constants ─────────────────────────────────────────────────────────────────

/** Access token TTL matches server (900 s = 15 min) */
const ACCESS_TOKEN_TTL_MS = 900 * 1000;

/** Refresh 2 minutes before token expiry (AC#29) */
const REFRESH_BUFFER_MS = 2 * 60 * 1000;

// ── Context ───────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ── Provider ──────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    organisation: null,
    membership: null,
    organisations: [],
    isAuthenticated: false,
    isLoading: true,
    degraded: false,
  });

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Schedule an access token refresh before expiry */
  const scheduleRefresh = useCallback((expiresAt: Date) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    const msUntilRefresh = Math.max(
      0,
      expiresAt.getTime() - Date.now() - REFRESH_BUFFER_MS,
    );

    refreshTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
        });

        if (res.ok) {
          const data = (await res.json()) as { accessToken?: string; expiresIn?: number };
          // Schedule next refresh based on the new token's TTL
          const ttlMs = (data.expiresIn ?? ACCESS_TOKEN_TTL_MS / 1000) * 1000;
          scheduleRefresh(new Date(Date.now() + ttlMs));
        } else {
          // Refresh failed — clear auth state; login prompt will appear
          setState((prev) => ({
            ...prev,
            isAuthenticated: false,
            user: null,
            organisation: null,
            membership: null,
          }));
        }
      } catch {
        // Network error — will be retried when user navigates
      }
    }, msUntilRefresh);
  }, []);

  /** Fetch current session from /api/auth/me and update state.
   *
   * Three outcomes are distinguished so the UI never shows a "Sign-in failed"
   * error for the normal "no session yet" path:
   *   - 2xx           → authenticated
   *   - 401           → expected unauth, clean state, NOT degraded
   *   - 5xx / network → real backend issue, mark degraded so LoginPage
   *                     shows a service-down message instead of auto-
   *                     triggering /api/auth/login.
   */
  const refreshAuth = useCallback(async (): Promise<void> => {
    let res: Response;
    try {
      res = await fetch('/api/auth/me', { credentials: 'include' });
    } catch {
      setState((prev) => ({
        ...prev,
        isAuthenticated: false,
        isLoading: false,
        user: null,
        organisation: null,
        membership: null,
        degraded: true,
      }));
      return;
    }

    if (res.ok) {
      const data = (await res.json()) as {
        user: AuthUser;
        organisation: AuthOrganisation | null;
        membership: AuthMembership | null;
        organisations: UserOrganisation[];
        current_org_id: string | null;
        degraded?: boolean;
      };

      setState({
        user: data.user,
        organisation: data.organisation,
        membership: data.membership,
        organisations: data.organisations ?? [],
        isAuthenticated: true,
        isLoading: false,
        degraded: data.degraded ?? false,
      });

      scheduleRefresh(new Date(Date.now() + ACCESS_TOKEN_TTL_MS));
      return;
    }

    const isExpectedUnauth = res.status === 401;
    setState((prev) => ({
      ...prev,
      isAuthenticated: false,
      isLoading: false,
      user: null,
      organisation: null,
      membership: null,
      degraded: !isExpectedUnauth,
    }));
  }, [scheduleRefresh]);

  // Load auth state on mount
  useEffect(() => {
    void refreshAuth();
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [refreshAuth]);

  /**
   * Initiate Work OS OAuth 2.0 + PKCE login.
   * Redirects the browser to the Work OS authorization URL.
   */
  const login = useCallback(
    async (
      redirectUri: string,
      connectionType: ConnectionType,
      orgId?: string,
    ): Promise<void> => {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          redirect_uri: redirectUri,
          connection_type: connectionType,
          ...(orgId ? { organisation_id: orgId } : {}),
        }),
      });

      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(err.message ?? 'Login initiation failed');
      }

      const data = (await res.json()) as { authorization_url: string; state: string };
      window.location.href = data.authorization_url;
    },
    [],
  );

  /** Revoke session and clear auth state — full reset.
   *
   * Server side: POST /api/auth/logout clears the refresh_token cookie
   * (Max-Age=0) and blacklists it in the DB.
   *
   * Client side: clear all auth-related browser storage and hard-navigate
   * to /login. The hard navigation discards every React Query cache,
   * context, and ref so the next session starts from a blank slate —
   * no stale data from the previous user can leak into the new login.
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch {
      // Ignore — proceed with client-side cleanup regardless. Cookies are
      // either already cleared, or will be on the next request.
    }

    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // Wipe any auth-adjacent keys the dashboard may have written. Listed
    // explicitly (rather than localStorage.clear()) to avoid nuking unrelated
    // user preferences like theme or sidebar state.
    try {
      localStorage.removeItem('archie_access_token');
      localStorage.removeItem('ss_access_token');
      sessionStorage.removeItem('auth_return_to');
    } catch {
      // localStorage can throw in private mode; the hard nav below still resets state.
    }

    // Clear in-memory state. The hard navigation below would normally make
    // this redundant, but we still clear so that any render cycle between
    // now and the navigation (and unit tests where window.location is stubbed)
    // sees the unauthenticated state.
    setState({
      user: null,
      organisation: null,
      membership: null,
      organisations: [],
      isAuthenticated: false,
      isLoading: false,
      degraded: false,
    });

    // Hard navigation — destroys the SPA, drops React Query cache, contexts,
    // refs, in-memory tokens. Next page load starts fresh on /login and the
    // bootstrap in <AuthProvider> sees no session.
    window.location.href = '/login';
  }, []);

  /** Switch the active organisation and re-fetch profile */
  const switchOrg = useCallback(
    async (orgId: string): Promise<void> => {
      const res = await fetch(`/api/auth/organisations/${orgId}/switch`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(err.message ?? 'Organisation switch failed');
      }

      const data = (await res.json()) as { accessToken?: string; expiresIn?: number };
      const ttlMs = (data.expiresIn ?? ACCESS_TOKEN_TTL_MS / 1000) * 1000;
      scheduleRefresh(new Date(Date.now() + ttlMs));

      await refreshAuth();
    },
    [refreshAuth, scheduleRefresh],
  );

  /**
   * Check if the authenticated user has a specific resource:action permission.
   *
   * Admin role bypasses all permission checks.
   * Falls back to checking the JWT-embedded permissions array.
   */
  const hasPermission = useCallback(
    (resource: string, action: string): boolean => {
      if (!state.isAuthenticated || !state.membership) return false;
      if (state.membership.role === 'admin') return true;
      return state.membership.permissions.some(
        (p) => p.resource === resource && p.action === action,
      );
    },
    [state.isAuthenticated, state.membership],
  );

  const value = useMemo<AuthContextValue>(() => ({
    ...state,
    login,
    logout,
    switchOrg,
    hasPermission,
    refreshAuth,
  }), [state, login, logout, switchOrg, hasPermission, refreshAuth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

/** Convenience hook for permission checks */
export function useHasPermission(resource: string, action: string): boolean {
  const { hasPermission } = useAuth();
  return hasPermission(resource, action);
}
